import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { WidgetApp } from './WidgetApp';

export const RULECMS_WIDGET_TAG = 'rulecms-widget';

const OBSERVED = ['token', 'published-key', 'endpoint'] as const;

/**
 * Custom element that hosts the RuleCMS React renderer in light DOM so host
 * CSS variables (brand tokens) and page fonts still apply.
 *
 * Usage:
 * ```html
 * <script src="…/widget-custom-element.iife.js"></script>
 * <rulecms-widget
 *   token="…"
 *   published-key="envId---widget-…"
 * ></rulecms-widget>
 * ```
 */
export class RuleCMSWidgetElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return [...OBSERVED];
  }

  #root: Root | null = null;
  #mountNode: HTMLDivElement | null = null;

  connectedCallback(): void {
    this.#ensureMount();
    this.#render();
  }

  disconnectedCallback(): void {
    this.#root?.unmount();
    this.#root = null;
    this.#mountNode = null;
  }

  attributeChangedCallback(): void {
    if (this.isConnected) {
      this.#render();
    }
  }

  /** JS property mirrors for frameworks that set properties instead of attributes. */
  get token(): string {
    return this.getAttribute('token') ?? '';
  }
  set token(value: string) {
    this.#setOrRemove('token', value);
  }

  get publishedKey(): string {
    return this.getAttribute('published-key') ?? '';
  }
  set publishedKey(value: string) {
    this.#setOrRemove('published-key', value);
  }

  get endpoint(): string {
    return this.getAttribute('endpoint') ?? '';
  }
  set endpoint(value: string) {
    this.#setOrRemove('endpoint', value);
  }

  #setOrRemove(name: string, value: string): void {
    if (value == null || value === '') {
      this.removeAttribute(name);
    } else {
      this.setAttribute(name, value);
    }
  }

  #ensureMount(): void {
    if (this.#mountNode && this.#root) {
      return;
    }
    // Light DOM: host page CSS variables / fonts remain available to the widget.
    this.#mountNode = document.createElement('div');
    this.#mountNode.setAttribute('data-rulecms-widget-root', '');
    this.replaceChildren(this.#mountNode);
    this.#root = createRoot(this.#mountNode);
  }

  #render(): void {
    if (!this.#root) {
      return;
    }
    const token = this.getAttribute('token') ?? '';
    const publishedKey = this.getAttribute('published-key') ?? '';
    const endpoint = this.getAttribute('endpoint') ?? undefined;

    this.#root.render(
      <WidgetApp
        token={token}
        publishedKey={publishedKey}
        endpoint={endpoint || undefined}
      />
    );
  }
}

/** Registers `<rulecms-widget>` once. Safe to call multiple times. */
export function define(): void {
  if (typeof customElements === 'undefined') {
    return;
  }
  if (!customElements.get(RULECMS_WIDGET_TAG)) {
    customElements.define(RULECMS_WIDGET_TAG, RuleCMSWidgetElement);
  }
}
